import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";

const NotFound = lazy(() => import("./pages/NotFound"));
const SaltSupply = lazy(() => import("./pages/SaltSupply"));
const PlantFinder = lazy(() => import("./pages/PlantFinder"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const AIReceptionist = lazy(() => import("./pages/AIReceptionist"));
const MiniWeatherStation = lazy(() => import("./pages/MiniWeatherStation"));
const KujoApparel = lazy(() => import("./pages/KujoApparel"));
const PlantCalculator = lazy(() => import("./pages/PlantCalculator"));
const SaltCalculator = lazy(() => import("./pages/SaltCalculator"));
const StatePage = lazy(() => import("./pages/StatePage"));
const CityPage = lazy(() => import("./pages/CityPage"));
const CommercialSaltLanding = lazy(
  () => import("./pages/CommercialSaltLanding"),
);
const KujoProductPage = lazy(() => import("./pages/KujoProductPage"));
const Giveaway = lazy(() => import("./pages/Giveaway"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/salt-supply" element={<SaltSupply />} />
              <Route path="/plant-finder" element={<PlantFinder />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/service-areas" element={<ServiceAreas />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/ai-receptionist" element={<AIReceptionist />} />
              <Route
                path="/mini-weather-station"
                element={<MiniWeatherStation />}
              />
              <Route path="/kujo-apparel" element={<KujoApparel />} />
              <Route path="/plant-calculator" element={<PlantCalculator />} />
              <Route path="/salt-calculator" element={<SaltCalculator />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/service-areas/:state" element={<StatePage />} />
              <Route
                path="/service-areas/:state/:city"
                element={<CityPage />}
              />
              <Route
                path="/landing/commercial-salt-supply"
                element={<CommercialSaltLanding />}
              />
              <Route path="/giveaway" element={<Giveaway />} />
              <Route path="/kujo-apparel/:slug" element={<KujoProductPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
